import { useEffect, useState } from "react";
import axios from "axios";
import "../../.csscode/PopUpModal.css";
import { FormGroup } from "reactstrap";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Paper,
  Box,
} from "@mui/material";

import SucessModal from "../successModal/SuccessModal";
import CustomLoader from "../static/CustomLoader";

const Role = ({ selectedId, mode, onHide, setRefresh, existingRoles }) => {
  const apiUrl = localStorage.getItem("apiUrl");
  const dbTokenData = localStorage.getItem("dbTokenData");
  const headers = {
    Authorization: `Bearer ${dbTokenData}`,
    "Content-Type": "application/json",
  };

  const [code, setCode] = useState("");
  const [role, setRole] = useState("");
  const [active, setActive] = useState(true);
  const [functioncodes, setFunctioncodes] = useState([]);
  const [functionOptions, setFunctionOptions] = useState([]);

  const [loading, setLoading] = useState(false);
  const [showSucessModal, setShowSucessModal] = useState(false);
  const [sucessMessage, setSucessMessage] = useState("");
  const [title, setTitle] = useState("");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchFunctions();
    if (mode === "U") {
      fetchRoleDetails();
    }
  }, [mode]);

  const fetchFunctions = async () => {
    try {
      const response = await axios.get(`${apiUrl}functions`, { headers });
      setFunctionOptions(
        response.data.map((f) => ({ value: f.code, label: f.functionality })),
      );
    } catch (err) {
      toast.error("Failed to fetch functions.");
    }
  };

  const fetchRoleDetails = async () => {
    if (!selectedId) return;
    setLoading(true);
    try {
      const response = await axios.get(`${apiUrl}role/code/${selectedId}`, {
        headers,
      });
      if (response.data.cd === 1 || !response.data.length) {
        toast.warning("Role not found.");
        return;
      }
      const first = response.data[0];
      setCode(first.code);
      setRole(first.role);
      setActive(!!first.active);
      setFunctioncodes(response.data.map((r) => r.functionCode));
    } catch (err) {
      toast.error("Failed to fetch role details.");
    } finally {
      setLoading(false);
    }
  };
  const validate = () => {
    let errs = {};

    const isSameGroup = (rcode, rrole) => rcode === code && rrole === role;

    // Check for duplicates only in Add mode
    const duplicateCode = existingRoles?.some(
      (r) => r.code === code && (mode === "A" || !isSameGroup(r.code, r.role)),
    );

    if (!code) errs.code = "Code is required";
    else if (duplicateCode) errs.code = "Code already exists";

    if (!role) errs.role = "Role is required";

    if (active !== true && active !== false)
      errs.active = "Active status is required";

    if (!Array.isArray(functioncodes) || functioncodes.length === 0)
      errs.functioncodes = "Select at least one function";

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      const payload = functioncodes.map((funcCode) => ({
        id: 0,
        code,
        role,
        functionCode: funcCode,
        disfields: "",
        active: active ? 1 : 0,
      }));

      const method = mode === "A" ? axios.post : axios.put;
      const url = `${apiUrl}role`;

      const response = await method(url, payload, { headers });

      if (response.data.cd === 1) {
        setSucessMessage(response.data.description);
        setTitle("⚠️ Failed");
      } else {
        setSucessMessage(response.data.description);
        setTitle("✔️ Success");
      }
      setShowSucessModal(true);
    } catch (err) {
      toast.error("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const clearFields = () => {
    setCode("");
    setRole("");
    setActive(true);
    setFunctioncodes([]);
  };

  const handleCloseSucessModal = () => {
    setShowSucessModal(false);
    setSucessMessage("");
    setTitle("");
    onHide();
    setRefresh(true);
  };

  return (
    <>
      <SucessModal
        show={showSucessModal}
        onClose={handleCloseSucessModal}
        headerText={title}
        message={sucessMessage}
      />

      <div className="popupmodal-maindiv">
        {/* Code */}

        <b>
          <label>Code</label>
        </b>
        <input
          required
          placeholder="Enter Code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          className="form-control mb-2"
          disabled={mode === "U"} // disable in update
        />
        {errors.code && <span className="text-danger">{errors.code}</span>}

        <b>
          <label>Role</label>
        </b>
        <input
          required
          placeholder="Enter Role"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          className="form-control mb-2"
        />
        {errors.role && <span className="text-danger">{errors.role}</span>}

        {/* Active */}

        <b>
          <label>Active</label>
        </b>
        <select
          className="form-control"
          value={active}
          onChange={(e) => setActive(e.target.value === "true")}
        >
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
        {errors.active && <span className="text-danger">{errors.active}</span>}

        <b>
          <Box
            sx={{
              mt: 2,
            }}
          ></Box>
          <label>Functions</label>
        </b>

        <TableContainer
          component={Paper}
          sx={{
            maxHeight: 260,
            overflowY: "auto",
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f1f5f9" }}>
                <TableCell
                  sx={{ width: "20%", fontWeight: 700, color: "white" }}
                >
                  Select
                </TableCell>
                <TableCell
                  sx={{ width: "80%", fontWeight: 700, color: "white" }}
                >
                  Function
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {functionOptions.map((opt) => (
                <TableRow key={opt.value} hover>
                  <TableCell sx={{ width: "20%" }}>
                    <Checkbox
                      checked={functioncodes.includes(opt.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFunctioncodes([...functioncodes, opt.value]);
                        } else {
                          setFunctioncodes(
                            functioncodes.filter((f) => f !== opt.value),
                          );
                        }
                      }}
                    />
                  </TableCell>

                  <TableCell sx={{ width: "80%" }}>{opt.label}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {errors.functioncodes && (
          <span className="text-danger">{errors.functioncodes}</span>
        )}

        {/* Buttons */}
        <div className="bottombtn">
          <button
            className="btn btn-success"
            disabled={loading}
            onClick={handleSubmit}
          >
            Save
          </button>
          {loading && <CustomLoader />}
          <button className="btn btn-danger" onClick={clearFields}>
            Reset
          </button>
        </div>
      </div>
    </>
  );
};

export default Role;
